<template>
  <div class="sap-downloads-container">

    <!-- ================= HEADER ================= -->
    <div class="page-header">
      <div class="d-flex flex-column">
        <div class="d-flex align-center flex-wrap">
          <div class="d-flex align-center">
            <v-avatar size="48" color="indigo-darken-2" class="mr-3" variant="tonal">
              <v-icon color="white" size="28">mdi-download-box</v-icon>
            </v-avatar>
            <div>
              <h1 class="text-h4 font-weight-bold text-grey-darken-4 mb-1">Painel SAP Downloads</h1>
              <div class="text-caption text-grey">
                Gerenciar downloads de planilhas Excel e confirmação de importação
              </div>
            </div>
          </div>
          
          <v-spacer />
          
          <div class="d-flex align-center mt-2 mt-sm-0">
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="voltar"
              class="text-none mr-2"
              prepend-icon="mdi-arrow-left"
            >
              Voltar
            </v-btn>
            
            <v-btn
              color="indigo-darken-2"
              variant="tonal"
              @click="fetchData"
              :loading="loading"
              prepend-icon="mdi-refresh"
            >
              Atualizar
            </v-btn>
          </div>
        </div>
      </div>
    </div>

    <!-- ================= ESTATÍSTICAS ================= -->
    <v-row class="mb-4">
      <v-col cols="6" sm="2">
        <v-card class="stat-card" elevation="2" @click="filtrarPorStatus('PENDENTE')">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="orange" class="mb-1">mdi-clock-outline</v-icon>
            <div class="text-h5 font-weight-bold text-orange">{{ estatisticas.pendentes || 0 }}</div>
            <div class="text-caption text-grey">Pendentes</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card class="stat-card" elevation="2" @click="filtrarPorStatus('BAIXADO')">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="blue" class="mb-1">mdi-download</v-icon>
            <div class="text-h5 font-weight-bold text-blue">{{ estatisticas.baixados || 0 }}</div>
            <div class="text-caption text-grey">Baixados</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card class="stat-card" elevation="2" @click="filtrarPorStatus('IMPORTADO')">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="green" class="mb-1">mdi-check-circle</v-icon>
            <div class="text-h5 font-weight-bold text-green">{{ estatisticas.importados || 0 }}</div>
            <div class="text-caption text-grey">Importados</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card class="stat-card" elevation="2" @click="filtrarPorStatus('CANCELADO')">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="grey" class="mb-1">mdi-cancel</v-icon>
            <div class="text-h5 font-weight-bold text-grey">{{ estatisticas.cancelados || 0 }}</div>
            <div class="text-caption text-grey">Cancelados</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="2">
        <v-card class="stat-card" elevation="2" @click="limparFiltros">
          <v-card-text class="text-center pa-3">
            <v-icon size="28" color="indigo" class="mb-1">mdi-file-excel</v-icon>
            <div class="text-h5 font-weight-bold text-indigo">{{ estatisticas.total || 0 }}</div>
            <div class="text-caption text-grey">Total</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ================= FILTROS ================= -->
    <v-card class="filters-card mb-4" elevation="2">
      <v-card-title class="filters-header pa-4">
        <div class="d-flex align-center w-100">
          <div class="d-flex align-center cursor-pointer flex-grow-1" @click="toggleFilters">
            <v-icon class="mr-2" color="white">mdi-filter-variant</v-icon>
            <span class="filters-title">Filtros</span>
            
            <div class="d-flex align-center gap-2 ml-4">
              <v-chip variant="tonal" size="small">
                {{ pagination.total }} arquivos
              </v-chip>
              
              <v-chip 
                v-if="hasActiveFilters"
                color="warning" 
                variant="tonal" 
                size="small"
              >
                {{ activeFiltersCount }} filtro(s)
              </v-chip>
            </div>
          </div>
          
          <div class="d-flex align-center gap-2 mr-2">
            <v-btn
              color="white"
              variant="flat"
              size="small"
              @click="limparFiltros"
              :disabled="!hasActiveFilters"
            >
              <v-icon size="18" :color="hasActiveFilters ? 'grey-darken-2' : 'grey-lighten-1'">mdi-filter-off</v-icon>
              <span class="ml-2" :class="hasActiveFilters ? 'text-grey-darken-2' : 'text-grey-lighten-1'">Limpar</span>
            </v-btn>
          </div>
          
          <v-btn
            icon
            variant="text"
            size="small"
            color="white"
            @click="toggleFilters"
          >
            <v-icon size="20" :class="{ 'rotate-180': filtersExpanded }">
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </div>
      </v-card-title>
      
      <v-expand-transition>
        <div v-show="filtersExpanded">
          <v-divider />
          <v-card-text class="pa-4">
            <v-row dense>
              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.status"
                  :items="statusOptions"
                  label="Status"
                  placeholder="Todos os status"
                  prepend-inner-icon="mdi-flag"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @update:model-value="fetchData"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-select
                  v-model="filtros.tipo_origem"
                  :items="tipoOrigemOptions"
                  label="Tipo de Origem"
                  placeholder="Todas as origens"
                  prepend-inner-icon="mdi-tag"
                  variant="outlined"
                  clearable
                  hide-details
                  density="compact"
                  @update:model-value="fetchData"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.data_inicio"
                  label="Data Início"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                  variant="outlined"
                  hide-details
                  density="compact"
                  @change="fetchData"
                />
              </v-col>

              <v-col cols="12" md="3">
                <v-text-field
                  v-model="filtros.data_fim"
                  label="Data Fim"
                  type="date"
                  prepend-inner-icon="mdi-calendar"
                  variant="outlined"
                  hide-details
                  density="compact"
                  @change="fetchData"
                />
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- ================= TABELA DE ARQUIVOS ================= -->
    <v-card elevation="2">
      <v-data-table
        :headers="headers"
        :items="arquivos"
        :loading="loading"
        :items-per-page="pagination.per_page"
        :page="pagination.current_page"
        class="elevation-0"
        hover
      >
        <!-- Nome do Arquivo -->
        <template v-slot:item.nome_arquivo="{ item }">
          <div class="d-flex align-center">
            <v-icon size="24" color="green" class="mr-2">mdi-microsoft-excel</v-icon>
            <div>
              <div class="font-weight-medium">{{ item.nome_arquivo }}</div>
              <div class="text-caption text-grey">ID: {{ item.id }}</div>
            </div>
          </div>
        </template>

        <!-- Tipo de Origem -->
        <template v-slot:item.tipo_origem="{ item }">
          <v-chip 
            :color="getTipoOrigemColor(item.tipo_origem)" 
            size="small"
            variant="tonal"
          >
            {{ formatTipoOrigem(item.tipo_origem) }}
          </v-chip>
        </template>

        <!-- Data de Geração -->
        <template v-slot:item.data_geracao="{ item }">
          <div>
            <div>{{ formatarData(item.data_geracao) }}</div>
            <div class="text-caption text-grey">{{ item.usuario_geracao || '-' }}</div>
          </div>
        </template>

        <!-- Último Download -->
        <template v-slot:item.data_ultimo_download="{ item }">
          <div v-if="item.data_ultimo_download">
            <div>{{ formatarData(item.data_ultimo_download) }}</div>
            <div class="text-caption text-grey">{{ item.usuario_ultimo_download || '-' }}</div>
          </div>
          <span v-else class="text-grey">-</span>
        </template>

        <!-- Status -->
        <template v-slot:item.status_importacao="{ item }">
          <v-chip 
            :color="getStatusColor(item.status_importacao)" 
            size="small"
            variant="flat"
          >
            <v-icon size="14" class="mr-1">{{ getStatusIcon(item.status_importacao) }}</v-icon>
            {{ formatStatus(item.status_importacao) }}
          </v-chip>
        </template>

        <!-- Importação SAP -->
        <template v-slot:item.data_importacao_sap="{ item }">
          <div v-if="item.data_importacao_sap">
            <div>{{ formatarData(item.data_importacao_sap) }}</div>
            <div class="text-caption text-grey">por {{ item.usuario_importacao_sap }}</div>
          </div>
          <span v-else class="text-grey">-</span>
        </template>

        <!-- Ações -->
        <template v-slot:item.acoes="{ item }">
          <div class="d-flex gap-1">
            <!-- Download -->
            <v-btn
              icon
              size="small"
              variant="text"
              color="green"
              @click="baixarArquivo(item)"
              :loading="downloadingId === item.id"
              :disabled="item.status_importacao === 'CANCELADO'"
            >
              <v-icon>mdi-download</v-icon>
              <v-tooltip activator="parent">Baixar Excel</v-tooltip>
            </v-btn>

            <!-- Confirmar Importação (disponível para PENDENTE e BAIXADO) -->
            <v-btn
              v-if="item.status_importacao === 'PENDENTE' || item.status_importacao === 'BAIXADO'"
              icon
              size="small"
              variant="text"
              color="green"
              @click="abrirDialogConfirmar(item)"
            >
              <v-icon>mdi-check-circle</v-icon>
              <v-tooltip activator="parent">Confirmar Importação SAP</v-tooltip>
            </v-btn>

            <!-- Cancelar (disponível para PENDENTE e BAIXADO) -->
            <v-btn
              v-if="item.status_importacao === 'PENDENTE' || item.status_importacao === 'BAIXADO'"
              icon
              size="small"
              variant="text"
              color="red"
              @click="abrirDialogCancelar(item)"
            >
              <v-icon>mdi-cancel</v-icon>
              <v-tooltip activator="parent">Cancelar Arquivo</v-tooltip>
            </v-btn>

            <!-- Ver Observação -->
            <v-btn
              v-if="item.observacao_importacao"
              icon
              size="small"
              variant="text"
              color="grey"
              @click="verObservacao(item)"
            >
              <v-icon>mdi-comment-text</v-icon>
              <v-tooltip activator="parent">Ver Observação</v-tooltip>
            </v-btn>
          </div>
        </template>

        <!-- Empty State -->
        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey-lighten-2">mdi-file-excel-box</v-icon>
            <div class="text-h6 text-grey mt-4">Nenhum arquivo encontrado</div>
            <div class="text-body-2 text-grey">Gere arquivos Excel na tela de Transferência de Estoque</div>
          </div>
        </template>
      </v-data-table>

      <!-- Paginação -->
      <v-divider />
      <div class="d-flex align-center justify-space-between pa-4">
        <v-select
          v-model="pagination.per_page"
          :items="[10, 15, 25, 50, 100]"
          label="Itens por página"
          variant="outlined"
          density="compact"
          hide-details
          style="max-width: 150px"
          @update:model-value="fetchData"
        />
        
        <v-pagination
          v-model="pagination.current_page"
          :length="pagination.last_page"
          :total-visible="5"
          density="compact"
          @update:model-value="fetchData"
        />
      </div>
    </v-card>

    <!-- ================= DIALOG CONFIRMAR IMPORTAÇÃO ================= -->
    <v-dialog v-model="dialogConfirmar" max-width="500">
      <v-card>
        <v-card-title class="text-h6 bg-blue text-white">
          <v-icon class="mr-2">mdi-check-circle</v-icon>
          Confirmar Importação SAP
        </v-card-title>
        <v-card-text class="pt-4">
          <p class="mb-4">
            Confirmar que o arquivo <strong>{{ arquivoSelecionado?.nome_arquivo }}</strong> 
            foi importado com sucesso no SAP?
          </p>
          
          <v-text-field
            v-model="formConfirmar.usuario_importacao"
            label="Usuário que fez a importação"
            placeholder="Nome do usuário"
            prepend-inner-icon="mdi-account"
            variant="outlined"
            density="compact"
          />
          
          <v-textarea
            v-model="formConfirmar.observacao"
            label="Observação (opcional)"
            placeholder="Adicione uma observação..."
            prepend-inner-icon="mdi-comment"
            variant="outlined"
            rows="2"
            density="compact"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogConfirmar = false">Cancelar</v-btn>
          <v-btn 
            color="blue" 
            variant="flat" 
            @click="confirmarImportacao"
            :loading="executandoAcao"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>


    <!-- ================= DIALOG CANCELAR ================= -->
    <v-dialog v-model="dialogCancelar" max-width="500">
      <v-card>
        <v-card-title class="text-h6 bg-red text-white">
          <v-icon class="mr-2">mdi-cancel</v-icon>
          Cancelar Arquivo
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="warning" variant="tonal" class="mb-4">
            <strong>Atenção:</strong> Esta ação irá:
            <ul class="mt-2">
              <li>Marcar o arquivo como cancelado</li>
              <li>Liberar as transferências para nova exportação</li>
              <li>Excluir o arquivo físico do servidor</li>
            </ul>
          </v-alert>
          
          <p class="mb-4">
            Cancelar o arquivo <strong>{{ arquivoSelecionado?.nome_arquivo }}</strong>?
          </p>
          
          <v-textarea
            v-model="formCancelar.motivo"
            label="Motivo do cancelamento (opcional)"
            placeholder="Informe o motivo..."
            prepend-inner-icon="mdi-comment"
            variant="outlined"
            rows="2"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogCancelar = false">Voltar</v-btn>
          <v-btn 
            color="red" 
            variant="flat" 
            @click="cancelarArquivo"
            :loading="executandoAcao"
          >
            Cancelar Arquivo
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ================= DIALOG OBSERVAÇÃO ================= -->
    <v-dialog v-model="dialogObservacao" max-width="500">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon class="mr-2">mdi-comment-text</v-icon>
          Observação
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert 
            :type="arquivoSelecionado?.status_importacao === 'ERRO' ? 'error' : 'info'" 
            variant="tonal"
          >
            {{ arquivoSelecionado?.observacao_importacao }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="dialogObservacao = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import { useUserStore } from "@/store/userStore";
import { sapDownloadsService } from "@/services/sapDownloadsService";

const toast = useToast();

export default {
  name: "SapDownloads",
  
  data() {
    return {
      loading: false,
      arquivos: [],
      estatisticas: {},
      filtersExpanded: true,
      downloadingId: null,
      executandoAcao: false,
      
      // Filtros
      filtros: {
        status: null,
        tipo_origem: null,
        data_inicio: null,
        data_fim: null,
      },
      
      // Paginação
      pagination: {
        total: 0,
        per_page: 15,
        current_page: 1,
        last_page: 1,
      },
      
      // Opções de filtros
      statusOptions: [
        { title: 'Pendente', value: 'PENDENTE' },
        { title: 'Baixado', value: 'BAIXADO' },
        { title: 'Importado', value: 'IMPORTADO' },
        { title: 'Cancelado', value: 'CANCELADO' },
      ],
      
      tipoOrigemOptions: [
        { title: 'Transferência', value: 'TRANSFERENCIA' },
        { title: 'Entrada', value: 'ENTRADA' },
        { title: 'Saída', value: 'SAIDA' },
      ],
      
      // Headers da tabela
      headers: [
        { title: 'Arquivo', key: 'nome_arquivo', sortable: true },
        { title: 'Origem', key: 'tipo_origem', sortable: true, width: '110px' },
        { title: 'Geração', key: 'data_geracao', sortable: true, width: '130px' },
        { title: 'Status', key: 'status_importacao', sortable: true, width: '110px' },
        { title: 'Último Download', key: 'data_ultimo_download', sortable: true, width: '150px' },
        { title: 'Importação SAP', key: 'data_importacao_sap', sortable: true, width: '150px' },
        { title: 'Ações', key: 'acoes', sortable: false, width: '180px', align: 'center' },
      ],
      
      // Dialogs
      dialogConfirmar: false,
      dialogCancelar: false,
      dialogObservacao: false,
      
      // Arquivo selecionado
      arquivoSelecionado: null,
      
      // Formulários
      formConfirmar: {
        usuario_importacao: '',
        observacao: '',
      },
      formCancelar: {
        motivo: '',
      },
    };
  },
  
  computed: {
    userStore() {
      return useUserStore();
    },
    
    hasActiveFilters() {
      return Object.values(this.filtros).some(v => v !== null && v !== '');
    },
    
    activeFiltersCount() {
      return Object.values(this.filtros).filter(v => v !== null && v !== '').length;
    },
  },
  
  mounted() {
    this.fetchData();
    this.fetchEstatisticas();
  },
  
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await sapDownloadsService.listarArquivos({
          ...this.filtros,
          per_page: this.pagination.per_page,
          page: this.pagination.current_page,
        });
        
        if (response.success) {
          this.arquivos = response.dados;
          this.pagination.total = response.total;
          this.pagination.per_page = response.per_page;
          this.pagination.current_page = response.current_page;
          this.pagination.last_page = response.last_page;
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao carregar arquivos");
      } finally {
        this.loading = false;
      }
    },
    
    async fetchEstatisticas() {
      try {
        const response = await sapDownloadsService.estatisticas();
        if (response.success) {
          this.estatisticas = response.dados;
        }
      } catch (error) {
        console.error(error);
      }
    },
    
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },
    
    limparFiltros() {
      this.filtros = {
        status: null,
        tipo_origem: null,
        data_inicio: null,
        data_fim: null,
      };
      this.pagination.current_page = 1;
      this.fetchData();
    },
    
    filtrarPorStatus(status) {
      this.filtros.status = status;
      this.pagination.current_page = 1;
      this.fetchData();
    },
    
    voltar() {
      // Volta para a tela que chamou (Menu, Transferência, Entrada, Saída, etc)
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: "Menu" });
      }
    },
    
    // Formatações
    formatarData(data) {
      if (!data) return "-";
      const date = new Date(data);
      return date.toLocaleDateString("pt-BR");
    },
    
    formatarHora(data) {
      if (!data) return "";
      const date = new Date(data);
      return date.toLocaleTimeString("pt-BR", { hour: '2-digit', minute: '2-digit' });
    },
    
    formatStatus(status) {
      const map = {
        'PENDENTE': 'Pendente',
        'BAIXADO': 'Baixado',
        'IMPORTADO': 'Importado',
        'CANCELADO': 'Cancelado',
      };
      return map[status] || status;
    },
    
    formatTipoOrigem(tipo) {
      const map = {
        'TRANSFERENCIA': 'Transferência',
        'ENTRADA': 'Entrada',
        'SAIDA': 'Saída',
      };
      return map[tipo] || tipo;
    },
    
    getStatusColor(status) {
      const map = {
        'PENDENTE': 'orange',
        'BAIXADO': 'blue',
        'IMPORTADO': 'green',
        'CANCELADO': 'grey',
      };
      return map[status] || 'grey';
    },
    
    getStatusIcon(status) {
      const map = {
        'PENDENTE': 'mdi-clock-outline',
        'BAIXADO': 'mdi-download',
        'IMPORTADO': 'mdi-check',
        'CANCELADO': 'mdi-cancel',
      };
      return map[status] || 'mdi-help';
    },
    
    getTipoOrigemColor(tipo) {
      const map = {
        'TRANSFERENCIA': 'deep-orange',
        'ENTRADA': 'green',
        'SAIDA': 'blue',
      };
      return map[tipo] || 'grey';
    },
    
    // Ações
    async baixarArquivo(item) {
      this.downloadingId = item.id;
      try {
        const response = await sapDownloadsService.baixarArquivo(item.id, item.nome_arquivo);
        if (response.success) {
          toast.success("Download iniciado");
          // Atualizar lista e estatísticas após download
          await this.fetchData();
          await this.fetchEstatisticas();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao baixar arquivo");
      } finally {
        this.downloadingId = null;
      }
    },
    
    abrirDialogConfirmar(item) {
      this.arquivoSelecionado = item;
      this.formConfirmar = {
        usuario_importacao: this.userStore.user?.name || '',
        observacao: '',
      };
      this.dialogConfirmar = true;
    },
    
    async confirmarImportacao() {
      this.executandoAcao = true;
      try {
        const response = await sapDownloadsService.confirmarImportacao(
          this.arquivoSelecionado.id,
          this.formConfirmar
        );
        
        if (response.success) {
          toast.success("Importação confirmada com sucesso");
          this.dialogConfirmar = false;
          this.fetchData();
          this.fetchEstatisticas();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao confirmar importação");
      } finally {
        this.executandoAcao = false;
      }
    },
    
    abrirDialogCancelar(item) {
      this.arquivoSelecionado = item;
      this.formCancelar = { motivo: '' };
      this.dialogCancelar = true;
    },
    
    async cancelarArquivo() {
      this.executandoAcao = true;
      try {
        const response = await sapDownloadsService.cancelarArquivo(
          this.arquivoSelecionado.id,
          this.formCancelar
        );
        
        if (response.success) {
          toast.success("Arquivo cancelado com sucesso");
          this.dialogCancelar = false;
          this.fetchData();
          this.fetchEstatisticas();
        } else {
          toast.error(response.message);
        }
      } catch (error) {
        console.error(error);
        toast.error("Erro ao cancelar arquivo");
      } finally {
        this.executandoAcao = false;
      }
    },
    
    verObservacao(item) {
      this.arquivoSelecionado = item;
      this.dialogObservacao = true;
    },
  },
};
</script>

<style scoped>
.sap-downloads-container {
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 24px;
}

.stat-card {
  cursor: pointer;
  transition: all 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.filters-card .filters-header {
  background: linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%);
  color: white;
}

.filters-title {
  font-size: 1rem;
  font-weight: 500;
}

.rotate-180 {
  transform: rotate(180deg);
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}
</style>
